import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { X, Send, Loader2, MessageSquare, Trash2, RefreshCw, Bot } from "lucide-react";

/**
 * Right-side drawer showing a load's discussion thread + auto-posted system
 * events (status changes, actions taken via assistant).
 */
export default function LoadMessagesDrawer({ load, onClose }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!load?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/loads/${load.id}/messages`);
      setMessages(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load messages");
    } finally { setLoading(false); }
  }, [load?.id]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const post = async (e) => {
    e?.preventDefault();
    const body = input.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/loads/${load.id}/messages`, { body });
      setMessages((m) => [...m, data]);
      setInput("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send");
    } finally { setBusy(false); }
  };

  const remove = async (msg) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/loads/${load.id}/messages/${msg.id}`);
      setMessages((m) => m.filter((x) => x.id !== msg.id));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="load-messages-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-[440px] max-w-full h-full bg-white border-l border-gray-200 flex flex-col shadow-2xl">
        <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between bg-gray-900 text-white">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <MessageSquare className="w-4 h-4 text-orange-400" strokeWidth={2} />
              <div className="text-[10px] uppercase tracking-widest text-gray-400">Load Discussion</div>
            </div>
            <div className="font-display font-bold text-lg tracking-tight">{load?.bol_number}</div>
            <div className="text-[11px] font-mono text-gray-400 mt-0.5">
              Status: {load?.status} · {messages.length} message{messages.length === 1 ? "" : "s"}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={refresh}
              disabled={loading}
              title="Refresh"
              data-testid="messages-refresh"
              className="p-1.5 text-gray-400 hover:text-white disabled:opacity-40"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={onClose}
              data-testid="messages-close"
              aria-label="Close"
              className="p-1.5 text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
          {messages.length === 0 && !loading && (
            <div className="text-xs text-gray-500 font-mono text-center py-8">
              No messages yet. Start the conversation.
            </div>
          )}
          {messages.map((m) => (
            <MessageRow key={m.id} m={m} me={user} onDelete={remove} />
          ))}
        </div>

        <form onSubmit={post} className="p-3 border-t border-gray-200 bg-white flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); post(); } }}
            placeholder="Type a message… (Enter to send)"
            data-testid="messages-input"
            disabled={busy}
            rows={2}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 focus:outline-none focus:border-gray-900 font-mono resize-none disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={!input.trim() || busy}
            data-testid="messages-send"
            className="w-9 h-9 flex items-center justify-center bg-gray-900 hover:bg-orange-500 text-white disabled:opacity-40 disabled:hover:bg-gray-900 transition-colors flex-shrink-0"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </form>
      </div>
    </div>
  );
}

function MessageRow({ m, me, onDelete }) {
  const isSystem = m.kind === "system";
  const isMine = !isSystem && m.author_id === me?.id;
  const time = m.at ? new Date(m.at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

  if (isSystem) {
    return (
      <div className="flex justify-center" data-testid={`msg-system-${m.id}`}>
        <div className="max-w-[90%] text-[11px] font-mono bg-white border border-gray-200 text-gray-600 px-3 py-1.5 flex items-center gap-2">
          <Bot className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          <span className="flex-1">{m.body}</span>
          <span className="text-gray-400 whitespace-nowrap">{time}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"}`} data-testid={`msg-human-${m.id}`}>
      <div className={`max-w-[85%] ${isMine ? "text-right" : ""}`}>
        <div className="text-[10px] uppercase tracking-widest text-gray-500 font-mono mb-1">
          {isMine ? "You" : m.author_name || m.author_email} · {(m.author_role || "").replace(/_/g, " ")} · {time}
        </div>
        <div className={`inline-block px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
          isMine ? "bg-gray-900 text-white" : "bg-white text-gray-900 border border-gray-200"
        }`}>
          {m.body}
        </div>
        {(isMine || me?.role === "admin") && (
          <button
            onClick={() => onDelete(m)}
            data-testid={`msg-delete-${m.id}`}
            className="ml-2 text-[10px] text-gray-400 hover:text-red-600 inline-flex items-center gap-0.5 mt-1"
          >
            <Trash2 className="w-3 h-3" /> delete
          </button>
        )}
      </div>
    </div>
  );
}
